import React, { useState } from 'react';
import { useAdmin } from '../hooks/useAdmin';
import { InfoIcon } from './icons/Icons';
import { Modal, Button, Badge, Disclosure } from './ui';
import './DXFExportDialog.css';

/**
 * Backwards-compatible DXFExportDialog wrapper around the Modal primitive.
 *
 * Public props (unchanged):
 *   isOpen, onExport, onCancel, structureName
 *
 * Emits: onExport({ mode, source }) — same shape as before.
 */
const DXFExportDialog = ({
  isOpen,
  onExport,
  onCancel,
  structureName = 'Structure',
}) => {
  const { isAdmin } = useAdmin();
  const [selectedSource, setSelectedSource] = useState('both');
  const [selectedMode, setSelectedMode] = useState('2d');

  const handleExport = () => {
    onExport({ mode: selectedMode, source: selectedSource });
  };

  const sourceOptions = [
    {
      value: 'rooms',
      label: 'Rooms Collection',
      description: 'Export data from individual room documents',
    },
    {
      value: 'structure',
      label: 'Structure Data',
      description: 'Export data from structure.walls and structure.floors',
    },
    {
      value: 'both',
      label: 'Both Sources',
      description: 'Export combined data from both rooms and structure',
    },
  ];

  const modeOptions = [
    {
      value: '2d',
      label: '2D Floor Plan',
      description: 'Flat architectural drawing (recommended for AutoCAD/Revit)',
    },
    {
      value: '3d',
      label: '3D Model',
      description: 'Three-dimensional representation (experimental)',
    },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title="Export to DXF"
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExport}>
            Export DXF
          </Button>
        </>
      }
    >
      {/* Helper text with info icon */}
      <div className="dxf-export-info">
        <InfoIcon
          width={20}
          height={20}
          color="currentColor"
          className="dxf-export-info-icon"
        />
        <p>
          DXF file is a vector drawing interchange file format, developed by
          Autodesk, used to share 2D and 3D design data between different CAD
          programs.
        </p>
      </div>

      <p className="dxf-export-dialog-subtitle">
        Exporting: <strong>{structureName}</strong>
      </p>

      {/* Export mode selection (visible to all users) */}
      <div className="dxf-export-section">
        <p className="dxf-export-dialog-subtitle">Choose export mode:</p>

        <div className="dxf-export-mode-options">
          {modeOptions.map((option) => (
            <label
              key={option.value}
              className={`dxf-export-mode-option ${
                selectedMode === option.value ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                name="exportMode"
                value={option.value}
                checked={selectedMode === option.value}
                onChange={(e) => setSelectedMode(e.target.value)}
              />
              <div className="dxf-export-mode-info">
                <span className="dxf-export-mode-label">{option.label}</span>
                <span className="dxf-export-mode-description">
                  {option.description}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Admin-only data source selection. */}
      {isAdmin && (
        <div className="dxf-export-advanced">
          <Disclosure
            summary={
              <span className="dxf-export-advanced-summary">
                <span className="dxf-export-advanced-label">
                  Advanced Options
                </span>
                <Badge variant="default">Admin</Badge>
              </span>
            }
          >
            <p className="dxf-export-advanced-panel-help">
              Choose data source:
            </p>
            <div className="dxf-export-source-options">
              {sourceOptions.map((option) => (
                <label
                  key={option.value}
                  className={`dxf-export-source-option ${
                    selectedSource === option.value ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="exportSource"
                    value={option.value}
                    checked={selectedSource === option.value}
                    onChange={(e) => setSelectedSource(e.target.value)}
                  />
                  <div className="dxf-export-source-info">
                    <span className="dxf-export-source-label">
                      {option.label}
                    </span>
                    <span className="dxf-export-source-description">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </Disclosure>
        </div>
      )}
    </Modal>
  );
};

export default DXFExportDialog;
